// src/patients/lib/chart-timeline.ts
//
// The patient chart's Timeline (Andrew, 4 Nov: Samer's demo "Timeline" view, under the patient chart, replacing Overview;
// switch `chartTimeline`). Pure: the existing reads in, dated marks and bars out. Nothing here is invented: a lane with no
// data source says so (an empty state and a gap), and a value the backend does not send is never filled in.
//
// Lanes and their sources (all existing reads, nothing new on the backend):
//   Visits      POST /api/Appointment/ForPatient (includePrevious): one mark per appointment, cancelled ones left out;
//   Labs        GET  /api/Patients/{id}/LabReports?includeHistory=true: one mark per current report, its worst flag
//               (H/L → attention, HH/LL/CRIT/PANIC → critical); a lab request with no report yet is a "pending" mark
//               (POST /api/Patients/GetPatientLabDetails);
//   Imaging     GET  /api/Patients/GetLabOrderByPatientId, the radiology entries: ORDERS only, each a pending study mark.
//               There is no imaging RESULT read (a gap, said on the lane);
//   Medications GET  /api/Patients/GetPatientMedicationDetails: one bar per medicine. Start: `startDate` (Scrips.Patient
//               adds it), else the created date; end: `endDate` (the stop), else start + duration when a duration is
//               recorded (marked computed), else open (still on the list);
//   Plan        GET  /api/Patients/{id}/care-plans: one bar per plan over its period.

import { clinicDayOf } from '@/patients/lib/lab-trend-cards';

export type TimelineLane = 'visits' | 'labs' | 'imaging' | 'medications' | 'plan';
export const TIMELINE_LANES: readonly { id: TimelineLane; title: string }[] = [
  { id: 'visits', title: 'Visits' },
  { id: 'labs', title: 'Labs' },
  { id: 'imaging', title: 'Imaging & studies' },
  { id: 'medications', title: 'Medications' },
  { id: 'plan', title: 'Plan & interventions' },
];

export type TimelineRange = '1Y' | '3Y' | '5Y' | 'All';
export const TIMELINE_RANGES: readonly TimelineRange[] = ['1Y', '3Y', '5Y', 'All'];

export type MarkKind = 'visit' | 'lab' | 'study' | 'pending';
export type MarkSeverity = 'normal' | 'attention' | 'critical';

export interface TimelineMark {
  id: string;
  lane: 'visits' | 'labs' | 'imaging';
  t: number;
  kind: MarkKind;
  severity: MarkSeverity;
  /** What the mark is, for its title and its screen-reader name ("Lipid panel · 28 Apr 2026 · above range"). */
  label: string;
  /** A flagged lab result: below the range ("down", every flag L) or above it ("up", any other flag). */
  dir?: 'up' | 'down';
}

/** The demo's ten mark shapes (scrips-clinician-demo timeline, Antonin's catalog TimelineSwimlane): the ones the reads can fill. */
export type MarkShape = 'dot' | 'ring' | 'up' | 'down' | 'alert' | 'square' | 'hollow-dot' | 'hollow-square';
/** A mark's shape, as the demo picks it: a visit is a dot; a lab result a ring, a triangle when flagged (its direction), an alert
 *  when critical; an order with no result yet is hollow (dot for labs, square for imaging, whose results have no read). */
export function markShape(m: Pick<TimelineMark, 'kind' | 'severity' | 'dir'>): MarkShape {
  if (m.kind === 'visit') return 'dot';
  if (m.kind === 'study') return 'hollow-square';
  if (m.kind === 'pending') return 'hollow-dot';
  if (m.severity === 'critical') return 'alert';
  if (m.severity === 'attention') return m.dir === 'down' ? 'down' : 'up';
  return 'ring';
}

export interface TimelineBar {
  id: string;
  lane: 'medications' | 'plan';
  start: number;
  /** null: still running (drawn to today). */
  end: number | null;
  label: string;
  /** The end is start + the recorded duration, not a recorded end date. */
  endComputed: boolean;
}

const DAY = 86_400_000;
const time = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};
const day = (t: number) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** A lab flag's weight, as the demo reads it: HH / LL / AA (HL7 critical abnormal) / critical / panic is critical; H / L / A /
 *  abnormal is attention. */
export function flagSeverity(flags: readonly (string | null | undefined)[]): MarkSeverity {
  const f = flags.filter((x): x is string => !!x).map((x) => x.trim().toUpperCase());
  if (f.some((x) => /^(HH|LL|AA|CRIT|CRITICAL|PANIC)/.test(x))) return 'critical';
  if (f.some((x) => /^(H|L|A|HIGH|LOW|ABN)/.test(x))) return 'attention';
  return 'normal';
}

// ── Visits ───────────────────────────────────────────────────────────────────────────────────────────
export interface VisitSource {
  id?: string | null;
  start?: string | null;
  status?: unknown;
  appointmentType?: { display?: string | null; name?: string | null } | null;
  reasonCode?: { display?: string | null } | null;
}
const statusWord = (s: unknown): string =>
  typeof s === 'string' ? s : s && typeof s === 'object' && 'code' in s ? String((s as { code?: unknown }).code ?? '') : '';

export function visitMarks(rows: readonly VisitSource[] | null | undefined): TimelineMark[] {
  return (rows ?? []).flatMap((r, i) => {
    const t = time(r.start);
    if (t === null || /cancel/i.test(statusWord(r.status))) return [];
    const what = r.appointmentType?.display || r.appointmentType?.name || 'Visit';
    const why = r.reasonCode?.display ? ` · ${r.reasonCode.display}` : '';
    return [{ id: `visit:${r.id ?? i}`, lane: 'visits' as const, t, kind: 'visit' as const, severity: 'normal' as const, label: `${what} · ${day(t)}${why}` }];
  });
}

// ── Labs ─────────────────────────────────────────────────────────────────────────────────────────────
export interface LabReportSource {
  id?: string | null;
  patientLabRequestId?: string | null;
  isCurrent?: boolean | null;
  issuedAt?: string | null;
  performingLabName?: string | null;
  observations?: readonly { effectiveAt?: string | null; interpretation?: string | null; display?: string | null }[] | null;
}
export interface LabRequestSource {
  id?: string | null;
  orderDate?: string | null;
  orderName?: string | null;
  orderStatus?: string | null;
}

export function labMarks(reports: readonly LabReportSource[] | null | undefined, requests: readonly LabRequestSource[] | null | undefined): TimelineMark[] {
  const current = (reports ?? []).filter((r) => r.isCurrent !== false);
  const reported = new Set(current.map((r) => r.patientLabRequestId).filter(Boolean));
  const results = current.flatMap((r, i) => {
    const obs = r.observations ?? [];
    const t = time(obs.find((o) => o.effectiveAt)?.effectiveAt) ?? time(r.issuedAt);
    if (t === null) return [];
    const severity = flagSeverity(obs.map((o) => o.interpretation));
    const named = obs.map((o) => o.display).filter(Boolean).slice(0, 3).join(', ');
    const word = severity === 'critical' ? ' · critical result' : severity === 'attention' ? ' · result outside range' : '';
    const flags = obs.map((o) => (o.interpretation ?? '').trim().toUpperCase()).filter((f) => /^(H|L|A|HIGH|LOW|ABN)/.test(f));
    const dir: 'up' | 'down' = flags.length > 0 && flags.every((f) => f.startsWith('L')) ? 'down' : 'up';
    return [{ id: `lab:${r.id ?? i}`, lane: 'labs' as const, t, kind: 'lab' as const, severity, dir, label: `${named || 'Lab results'} · ${day(t)}${word}` }];
  });
  const pending = (requests ?? []).flatMap((q, i) => {
    const t = time(q.orderDate);
    if (t === null || (q.id && reported.has(q.id)) || /complet|result|final|cancel/i.test(q.orderStatus ?? '')) return [];
    return [{ id: `labreq:${q.id ?? i}`, lane: 'labs' as const, t, kind: 'pending' as const, severity: 'normal' as const, label: `${q.orderName || 'Lab order'} · ordered ${day(t)} · no result yet` }];
  });
  return [...results, ...pending];
}

// ── Imaging (orders only) ────────────────────────────────────────────────────────────────────────────
export interface OrderGroupSource {
  identifier?: string | null;
  createdDate?: string | null;
  isDeleted?: boolean;
  radiologyOrderRequest?: readonly { id?: string | null; categoryName?: string | null; isDeleted?: boolean | null; code?: { displayName?: string | null } | null }[] | null;
}
export function imagingMarks(groups: readonly OrderGroupSource[] | null | undefined): TimelineMark[] {
  return (groups ?? []).flatMap((g) => {
    const t = time(g.createdDate);
    if (t === null || g.isDeleted) return [];
    return (g.radiologyOrderRequest ?? [])
      .filter((o) => !o.isDeleted && /radiolog|imaging/i.test(o.categoryName ?? ''))
      .map((o, i) => ({
        id: `img:${o.id ?? `${g.identifier}:${i}`}`,
        lane: 'imaging' as const,
        t,
        kind: 'study' as const,
        severity: 'normal' as const,
        label: `${o.code?.displayName || 'Imaging order'} · ordered ${day(t)}`,
      }));
  });
}

// ── Medications ──────────────────────────────────────────────────────────────────────────────────────
export interface MedicationSource {
  identifier?: string | null;
  code?: { display?: string | null; shortDisplay?: string | null } | null;
  createdDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  duration?: string | null;
  durationType?: string | null;
  isDeleted?: boolean;
}
const UNIT_DAYS: Record<string, number> = { day: 1, days: 1, d: 1, week: 7, weeks: 7, w: 7, month: 30, months: 30, m: 30, year: 365, years: 365, y: 365 };
/** A recorded duration in days ("14" + "Days" → 14), or null when it is missing or not a number. */
export function durationDays(duration: string | null | undefined, type: string | null | undefined): number | null {
  const n = Number(String(duration ?? '').trim());
  const per = UNIT_DAYS[String(type ?? 'days').trim().toLowerCase()];
  return Number.isFinite(n) && n > 0 && per ? n * per : null;
}

export function medicationBars(rows: readonly MedicationSource[] | null | undefined): TimelineBar[] {
  return (rows ?? []).flatMap((m, i) => {
    if (m.isDeleted) return [];
    const start = time(m.startDate) ?? time(m.createdDate);
    if (start === null) return [];
    const recordedEnd = time(m.endDate);
    const days = recordedEnd === null ? durationDays(m.duration, m.durationType) : null;
    const end = recordedEnd ?? (days !== null ? start + days * DAY : null);
    const name = m.code?.shortDisplay || m.code?.display || 'Medication';
    return [{ id: `med:${m.identifier ?? i}`, lane: 'medications' as const, start, end, label: name, endComputed: recordedEnd === null && days !== null }];
  });
}

// ── Plan ─────────────────────────────────────────────────────────────────────────────────────────────
export interface PlanSource {
  id?: string | null;
  title?: string | null;
  status?: string | null;
  period?: { start: string | null; end: string | null } | null;
}
export function planBars(rows: readonly PlanSource[] | null | undefined): TimelineBar[] {
  return (rows ?? []).flatMap((p, i) => {
    const start = time(p.period?.start);
    if (start === null) return [];
    return [{ id: `plan:${p.id ?? i}`, lane: 'plan' as const, start, end: time(p.period?.end), label: p.title || 'Care plan', endComputed: false }];
  });
}

// ── The window and its axis ──────────────────────────────────────────────────────────────────────────
/** The window shown: the last 1, 3 or 5 years to today; "All" from the earliest event (a year back when there is none). */
export function rangeWindow(range: TimelineRange, now: number, earliest: number | null): { from: number; to: number } {
  const d = new Date(now);
  if (range === 'All') return { from: Math.min(earliest ?? now, Date.UTC(d.getUTCFullYear() - 1, d.getUTCMonth(), d.getUTCDate())), to: now };
  const years = range === '1Y' ? 1 : range === '3Y' ? 3 : 5;
  return { from: Date.UTC(d.getUTCFullYear() - years, d.getUTCMonth(), d.getUTCDate()), to: now };
}

/** The axis ticks: months for one year, else 1 January of each year inside the window. */
export function axisTicks(from: number, to: number, range: TimelineRange): { t: number; label: string }[] {
  const out: { t: number; label: string }[] = [];
  const s = new Date(from);
  if (range === '1Y') {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // A window that starts on the 1st (the demo's axis starts on 1 January) ticks that month too.
    const first = s.getUTCDate() === 1 && s.getUTCHours() === 0 ? 0 : 1;
    for (let m = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + first, 1)); m.getTime() <= to; m = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + 1, 1))) {
      out.push({ t: m.getTime(), label: m.getUTCMonth() === 0 ? `Jan ${m.getUTCFullYear()}` : MONTHS[m.getUTCMonth()] });
    }
    return out;
  }
  const firstYear = s.getUTCMonth() === 0 && s.getUTCDate() === 1 && s.getUTCHours() === 0 ? s.getUTCFullYear() : s.getUTCFullYear() + 1;
  for (let y = firstYear; Date.UTC(y, 0, 1) <= to; y += 1) out.push({ t: Date.UTC(y, 0, 1), label: String(y) });
  return out;
}

const DAY_MS = 86_400_000;
/** How far past 31 December the lanes may run for a booked visit, so one far-future booking cannot squeeze the window. */
export const LANE_END_CAP_DAYS = 90;

/**
 * Where the lanes end (the demo runs them past today, fading later items): 31 December of the clinic's year (23:59:59 in
 * Dubai, 19:59:59 UTC), or a later BOOKED visit, capped at LANE_END_CAP_DAYS after that 31 December. Only visits count: a
 * lab or imaging date after today is not a booking.
 */
export function laneEnd(now: number, marks: readonly Pick<TimelineMark, 'kind' | 't'>[]): number {
  const year = Number((clinicDayOf(now) ?? new Date(now).toISOString()).slice(0, 4));
  const yearEnd = Date.UTC(year, 11, 31, 19, 59, 59);
  const booked = marks.filter((m) => m.kind === 'visit' && m.t > now).map((m) => m.t);
  const latest = booked.length ? Math.max(...booked) : yearEnd;
  return Math.min(Math.max(yearEnd, latest), yearEnd + LANE_END_CAP_DAYS * DAY_MS);
}

/** The demo's "same week" key: the Monday of the CLINIC's calendar week (Dubai), as UTC midnight of that date. A result at
 *  01:00 on Monday in Dubai (21:00 UTC on Sunday) is in the new week. */
export const weekOf = (t: number): number => {
  const day = clinicDayOf(t) ?? new Date(t).toISOString().slice(0, 10);
  const d = new Date(`${day}T00:00:00Z`);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
};
